/**
 * DashboardPayments entity class
 */

/**
 * DashboardPaymentsFactory
 * returns DashboardPayments entity class
 *
 * @class DashboardPaymentsFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.DashboardPayments} class
 *
 * @ngdoc factory
 * @name mwApp.factory:DashboardPaymentsFactory
 */
/* @ngInject */
export default function DashboardPaymentsFactory ($http, Entity) {
  /* === DashboardPayments class === */

  /**
   * DashboardPayments model
   *
   * @class DashboardPayments
   * @classdesc DashboardPayments model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @return {models.DashboardPayments} DashboardPayments entity
   */
  function DashboardPayments () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  DashboardPayments.prototype = Object.create(Entity.prototype)
  DashboardPayments.prototype.constructor = DashboardPayments

  /* === DashboardPayments instance methods & fields === */

  Object.assign(DashboardPayments.prototype, {

    /**
     * instance fields
     *
     * @memberof models.DashboardPayments#
     * @type {Object}
     */
    _fields: {
      autopayPaymentsCount: [],
      allPaymentsCount: [],
      cashPaymentsCount: [],
      authorizePaymentsCount: [],
      checkPaymentsCount: [],
      externalPaymentsCount: [],
      ccppPaymentsCount: [],
      gr4vyPaymentsCount: [],
      goCardlessPaymentsCount: [],
      trueMoneyPaymentsCount: [],
      paypalPaymentsCount: [],

      autopayPaymentsGraph: [],
      paymentsBySystemTypeGraph: []
    },

    /**
     * url template
     *
     * @memberof models.DashboardPayments#
     * @type {String}
     */
    _urlTemplate: 'api/dashboard/widgets/payments',

    /**
     * public url template
     *
     * @memberof models.DashboardPayments#
     * @type {String}
     */
    _publicUrlTemplate: 'dashboard/widgets/payments',

    /**
     * Get service for payment
     *
     * @memberof models.DashboardPayments#
     * @private
     */
    _loadAggregatePayment: function (params, callback) {
      return DashboardPayments.load({
        params,
        url: '\\agg'
      }, { valueUnit: null, value: 0 })
        .then(({ valueUnit, value }) => callback.call(this, { valueUnit, value }))
    },

    /**
     * Get service for payment
     *
     * @memberof models.DashboardPayments#
     * @private
     */
    _loadAggregateGraphPayment: function (params) {
      return DashboardPayments.load({
        params,
        url: '\\agg\\graph'
      }, { graphView: null })
    },

    /**
     * Returns metrics for a given currency with data for a given aggregation type by payment system types
     *
     * @param currencyId
     * @param {DashboardSetup} settings
     * @param period
     * @return {Promise}
     */
    loadMetrics: function (currencyId, settings, period) {
      const params = { 'agg-type': 'SUM', currencyId }
      const results = []
      this.autopayPaymentsCount = []
      this.allPaymentsCount = []
      this.cashPaymentsCount = []
      this.authorizePaymentsCount = []
      this.checkPaymentsCount = []
      this.externalPaymentsCount = []
      this.ccppPaymentsCount = []
      this.gr4vyPaymentsCount = []
      this.goCardlessPaymentsCount = []
      this.trueMoneyPaymentsCount = []
      this.trueOnlinePaymentsCount = []
      this.trueMoveHPaymentsCount = []
      this.paypalPaymentsCount = []

      // TODO: rewrite to iterator Array[<payments>]

      settings.AUTOPAY_PAYMENTS_CARD.enabled &&
        results.push(this._loadAggregatePayment({
          ...params,
          autoPay: true,
          days: period
        }, (payload) => { this.autopayPaymentsCount.push(payload) }))

      settings.ALL_PAYMENTS_CARD.enabled &&
        results.push(this._loadAggregatePayment({
          ...params,
          days: period
        }, (payload) => { this.allPaymentsCount.push(payload) }))

      settings.CASH_PAYMENTS_CARD.enabled &&
        results.push(this._loadAggregatePayment({
          ...params,
          'system-type': 'CASH',
          days: period
        }, (payload) => { this.cashPaymentsCount.push(payload) }))

      settings.AUTHORIZE_PAYMENTS_CARD.enabled &&
        results.push(this._loadAggregatePayment({
          ...params,
          'system-type': 'AUTHORIZE_NET',
          days: period
        }, (payload) => { this.authorizePaymentsCount.push(payload) }))

      settings.CHECK_PAYMENTS_CARD.enabled &&
        results.push(this._loadAggregatePayment({
          ...params,
          'system-type': 'CHECK',
          days: period
        }, (payload) => { this.checkPaymentsCount.push(payload) }))

      settings.EXTERNAL_PAYMENTS_CARD.enabled &&
        results.push(this._loadAggregatePayment({
          ...params,
          'system-type': 'EXTERNAL_PAYMENTS',
          days: period
        }, (payload) => { this.externalPaymentsCount.push(payload) }))

      settings.CCPP_PAYMENTS_CARD.enabled &&
      results.push(this._loadAggregatePayment({
        ...params,
        'system-type': 'CCPP',
        days: period
      }, (payload) => { this.ccppPaymentsCount.push(payload) }))

      settings.GR4VY_PAYMENTS_CARD.enabled &&
      results.push(this._loadAggregatePayment({
        ...params,
        'system-type': 'GR4VY_GATEWAY',
        days: period
      }, (payload) => { this.gr4vyPaymentsCount.push(payload) }))

      settings.GOCARDLESS_PAYMENTS_CARD.enabled &&
      results.push(this._loadAggregatePayment({
        ...params,
        'system-type': 'GOCARDLESS',
        days: period
      }, (payload) => { this.goCardlessPaymentsCount.push(payload) }))

      settings.TRUE_MONEY_PAYMENTS_CARD.enabled &&
      results.push(this._loadAggregatePayment({
        ...params,
        'system-type': 'TRUE_MONEY',
        days: period
      }, (payload) => { this.trueMoneyPaymentsCount.push(payload) }))

      settings.TRUE_ONLINE_PAYMENTS_CARD.enabled &&
      results.push(this._loadAggregatePayment({
        ...params,
        'system-type': 'TRUE_ONLINE',
        days: period
      }, (payload) => { this.trueOnlinePaymentsCount.push(payload) }))

      settings.TRUE_MOVE_H_PAYMENTS_CARD.enabled &&
      results.push(this._loadAggregatePayment({
        ...params,
        'system-type': 'TRUE_MOVE_H',
        days: period
      }, (payload) => { this.trueMoveHPaymentsCount.push(payload) }))

      settings.PAYPAL_PAYMENTS_CARD.enabled &&
      results.push(this._loadAggregatePayment({
        ...params,
        'system-type': 'PAYPAL',
        days: period
      }, (payload) => { this.paypalPaymentsCount.push(payload) }))

      return Promise.all(results)
    }
  })

  /* === DashboardPayments static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(DashboardPayments, Entity)
  DashboardPayments._name = 'DashboardPayments'
  DashboardPayments.init()

  /**
   * Load extra for entity with filter payload
   *
   * @param url
   */
  DashboardPayments.loadPayloadExtra = (url) =>
    DashboardPayments.loadExtra(url)

  return DashboardPayments
}
